import { browserEffectRuntime } from "@crm/effect/client/runtime";

/**
 * Knowledge Drafts Page (Spec 059)
 *
 * 我的草稿：管理正在编辑的知识文章
 * 支持客户端搜索 + 分页
 */

import { P, usePermission } from "@crm/access/authorization/client";
import { useAuth } from "@crm/access/hooks/use-auth";
import { formatBeijingLocaleDateTime } from "@crm/shared/time/beijing-time";
import { InfiniteScrollTrigger, ListPageShell, PageHeader } from "@crm/ui/components/shared";
import { QueryFailureCard } from "@crm/ui/components/shared/query-failure-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crm/ui/components/ui/alert-dialog";
import { Badge } from "@crm/ui/components/ui/badge";
import { Button } from "@crm/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@crm/ui/components/ui/card";
import { SearchInput } from "@crm/ui/components/ui/search-input";
import { useDebounce } from "@crm/ui/hooks/use-debounce";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Data, Effect, Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { Edit, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useCourses } from "#app/courses";
import { useDeleteKnowledgeEntry } from "#app/knowledge/knowledge.mutations";
import {
  useKnowledgeEntriesInfinite,
  useKnowledgeEntryCourses,
  useKnowledgeEntryTags,
  useKnowledgeTags,
} from "#app/knowledge/knowledge.queries";
import { CreateDraftDialog, KnowledgeNav } from "#components/knowledge";

class KnowledgeDraftsRouteWorkflowError extends Data.TaggedError(
  "KnowledgeDraftsRouteWorkflowError",
)<{
  readonly message: string;
  readonly cause?: unknown;
}> {}

function toKnowledgeDraftsRouteWorkflowError(
  cause: unknown,
  fallbackMessage: string,
): KnowledgeDraftsRouteWorkflowError {
  return new KnowledgeDraftsRouteWorkflowError({
    message: cause instanceof Error ? cause.message : fallbackMessage,
    cause,
  });
}

function promiseKnowledgeDraftsRouteEffect<A>(
  run: () => PromiseLike<A>,
  fallbackMessage: string,
): Effect.Effect<A, KnowledgeDraftsRouteWorkflowError> {
  return Effect.tryPromise({
    try: run,
    catch: (cause) => toKnowledgeDraftsRouteWorkflowError(cause, fallbackMessage),
  });
}

export const Route = createFileRoute("/_protected/knowledge/drafts")({
  head: () => ({
  }),
  component: DraftsPage,
});

function DraftsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const canWriteAll = usePermission(P.KNOWLEDGE_ENTRY_WRITE_ALL);
  const canWriteAssigned = usePermission(P.KNOWLEDGE_ENTRY_WRITE_ASSIGNED);
  const canCreate = canWriteAll || canWriteAssigned;
  const [searchTerm, setSearchTerm] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const debouncedSearch = useDebounce(searchTerm, 300);

  // 删除 mutation
  const deleteEntryMutation = useDeleteKnowledgeEntry();

  // 获取草稿条目（服务端分页 + infinite scroll）
  // 权限过滤已在 handler 层处理：coach 只能看自己的 draft
  const {
    items: drafts,
    totalCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useKnowledgeEntriesInfinite({
    status: "draft",
    search: debouncedSearch || undefined,
  });

  // 获取标签
  const tagsResult = useKnowledgeTags();
  const tags = AsyncResult.value(tagsResult).pipe(Option.getOrElse(() => []));

  // 获取条目-标签关联
  const entryTagsResult = useKnowledgeEntryTags();
  const entryTags = AsyncResult.value(entryTagsResult).pipe(Option.getOrElse(() => []));

  // 获取课程
  const coursesResult = useCourses();
  const courses = AsyncResult.value(coursesResult).pipe(Option.getOrElse(() => []));

  // 获取条目-课程关联
  const entryCoursesResult = useKnowledgeEntryCourses();
  const entryCourses = AsyncResult.value(entryCoursesResult).pipe(Option.getOrElse(() => []));

  // 搜索变化
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  // 获取条目的标签
  const getEntryTags = (entryId: string) => {
    const tagIds = entryTags.filter((et) => et.entryId === entryId).map((et) => et.tagId);
    return tags.filter((t) => tagIds.includes(t.id));
  };

  // 获取条目的课程
  const getEntryCourses = (entryId: string) => {
    const courseIds = entryCourses.filter((ec) => ec.entryId === entryId).map((ec) => ec.courseId);
    return courses.filter((c) => courseIds.includes(c.id));
  };

  const handleDelete = () => {
    if (!deleteEntry) {
      return;
    }

    return browserEffectRuntime.runPromise(
      promiseKnowledgeDraftsRouteEffect(
        () => deleteEntryMutation.executePromise(deleteEntry.id),
        "删除草稿失败",
      ).pipe(
        Effect.tap(() =>
          Effect.sync(() => {
            toast.success("已删除");
            setDeleteEntry(null);
          }),
        ),
        Effect.catch(() => Effect.sync(() => toast.error("删除失败"))),
        Effect.asVoid,
      ),
    );
  };

  if (error !== null) {
    return (
      <ListPageShell>
        <KnowledgeNav />
        <div className="mt-4">
          <PageHeader description="管理你正在编辑的知识文章" title="我的草稿" />
        </div>
        <QueryFailureCard
          className="mt-6"
          description="草稿列表暂时无法获取，失败不会显示为暂无草稿。"
          onRetry={refetch}
          title="草稿加载失败"
        />
      </ListPageShell>
    );
  }

  return (
    <ListPageShell>
      <KnowledgeNav />
      <div className="mt-4 flex items-center justify-between">
        <PageHeader description="管理你正在编辑的知识文章" title="我的草稿" />
        {canCreate && (
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            新建草稿
          </Button>
        )}
      </div>

      {/* 搜索框 */}
      <div className="mt-6">
        <SearchInput
          className="max-w-sm"
          onChange={handleSearchChange}
          placeholder="搜索草稿..."
          value={searchTerm}
        />
      </div>

      {drafts.length === 0 && !isLoading ? (
        <div className="mt-8 py-12 text-center text-muted-foreground">
          <p>{debouncedSearch ? "没有找到匹配的草稿" : "暂无草稿"}</p>
          {!debouncedSearch && (
            <Link className="mt-2 inline-block text-primary hover:underline" to="/knowledge/pool">
              去认领池看看
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {drafts.map((entry) => {
              const entryTagList = getEntryTags(entry.id);
              const entryCourseList = getEntryCourses(entry.id);
              const isOwner = entry.claimedBy === auth.user?.staffId;
              return (
                <Card className="flex flex-col" key={entry.id}>
                  <CardHeader>
                    <CardTitle className="line-clamp-2 text-lg">{entry.title}</CardTitle>
                    <CardDescription className="flex flex-wrap gap-1">
                      {/* 课程分类 */}
                      {entryCourseList.map((course) => (
                        <Badge key={course.id} variant="default">
                          {course.name}
                        </Badge>
                      ))}
                      {/* 标签 */}
                      {entryTagList.map((tag) => (
                        <Badge key={tag.id} variant="secondary">
                          {tag.name}
                        </Badge>
                      ))}
                      {!isOwner && <Badge variant="outline">他人草稿</Badge>}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <p className="text-muted-foreground text-xs">
                      更新于:{" "}
                      {entry.updatedAt ? formatBeijingLocaleDateTime(entry.updatedAt) : "未知"}
                    </p>
                  </CardContent>
                  <CardFooter className="flex gap-2">
                    <Button
                      onClick={() =>
                        navigate({
                          to: "/knowledge/edit/$id",
                          params: { id: entry.id },
                        })
                      }
                      size="sm"
                      variant="default"
                    >
                      <Edit className="mr-1 h-4 w-4" />
                      编辑
                    </Button>
                    {(isOwner || canWriteAll) && (
                      <Button
                        onClick={() => setDeleteEntry({ id: entry.id, title: entry.title })}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                        删除
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          {/* Infinite scroll 触发器 */}
          <InfiniteScrollTrigger
            completedText={`已加载全部 ${totalCount} 条草稿`}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage ?? false}
            isFetchingNextPage={isFetchingNextPage}
            loadedCount={drafts.length}
            totalCount={totalCount}
          />
        </>
      )}

      {/* 创建草稿对话框 */}
      <CreateDraftDialog onOpenChange={setCreateDialogOpen} open={createDialogOpen} />

      {/* 删除确认对话框 */}
      <AlertDialog onOpenChange={() => setDeleteEntry(null)} open={!!deleteEntry}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除「{deleteEntry?.title}」吗？此操作不可恢复。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ListPageShell>
  );
}
